import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

import { CompanyService } from '../../services/company.service';
import { HeaderService } from '../../services/header.service';
import { VerificationStatusEnum } from '../../../common/enums/verification-status.enum';
import { finalize, forkJoin } from 'rxjs';
import { Messages, ValidationPatterns } from '../../../common/constants/messages';
import { NgxMaskDirective } from 'ngx-mask';
import { InvalidCharsPipe } from '../../../common/Helpers/invalid-chars.pipe';

@Component({
  selector: 'app-w9-business-verification',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, NgxSpinnerModule, NgxMaskDirective, DialogModule, RouterModule, ButtonModule, FileUploadModule
  ],
  templateUrl: './w9-business-verification.component.html',
  styleUrls: ['./w9-business-verification.component.css']
})
export class W9BusinessVerificationComponent implements OnInit {

  @ViewChild('fileUploadRef')
  fileUploadRef!: FileUpload;

  businessVerificationForm!: FormGroup;

  verificationStatus = 'Incomplete';

  selectedFile: File | null = null;

  loading = false;

  dialogSummary = '';
  dialogMsg = '';

  locListModalDlg = false;
  companyId: string = '';

  dialogType: 'normal' | 'unsaved' = 'normal';
  dialogAction: 'none' | 'redirectToAccountSettings' = 'none';

  fileSizeError = false;
  isFileValid = false;

  readonly maxFileSize = 5 * 1024 * 1024; // 5 MB

  private resolveGuard!: (value: boolean) => void;

  constructor(
    private ngx: NgxSpinnerService,
    private fb: FormBuilder,
    private spinner: NgxSpinnerService,
    private router: Router,
    private companyService: CompanyService,
    private headerService: HeaderService
  ) { }

  ngOnInit(): void {
    this.companyId = sessionStorage.getItem('companyId') || '';
    const companyName = sessionStorage.getItem('companyName');

    if (companyName) {
      this.headerService.setCompanyName(companyName);
    }

    this.businessVerificationForm = this.fb.group({
      ein: [
        '',
        [
          Validators.required,
          Validators.pattern(ValidationPatterns.EIN)
        ]
      ],

      w9File: [
        null,
        Validators.required
      ]
    });


    this.loadBusinessVerification();

  }

  triggerFileSelect(): void {

    this.fileUploadRef.choose();

  }


  loadBusinessVerification(): void {

    this.ngx.show();

    forkJoin({
      ein: this.companyService.getCompanyEIN(this.companyId),
      w9: this.companyService.getW9(this.companyId)
    })
      .pipe(
        finalize(() => this.ngx.hide())
      )
      .subscribe({
        next: (res) => {

          const einControl = this.businessVerificationForm.get('ein');

          // Populate EIN returned from API
          einControl?.setValue(res.ein ?? '');

          // Re-evaluate validation
          einControl?.updateValueAndValidity();

          // W9 Status
          if (res.w9) {

            const status = Number(res.w9.verificationStatus);

            switch (status) {

              case VerificationStatusEnum.Approved:
                this.verificationStatus = 'Complete';
                break;
              case VerificationStatusEnum.Pending:
                this.verificationStatus = 'Pending';
                break
              case VerificationStatusEnum.Rejected:
              default:
                this.verificationStatus = 'Incomplete';
                break;
            }
          }
          else {
            this.verificationStatus = 'Incomplete';
          }
        },

        error: (err) => {

          this.dialogSummary = Messages.ErrorTitle;
          this.dialogMsg = Messages.ErrorInAPICall;
          this.locListModalDlg = true;

        }
      });

  }

  onFileSelect(event: any): void {

    this.fileSizeError = false;
    this.isFileValid = false;
    const allowedExtensions = ['pdf', 'jpg', 'jpeg'];

    if (!event.files?.length) {
      return;
    }

    const file = event.files[0];
    const extension = file.name.split('.').pop()?.toLowerCase();

    // Explicit 5 MB validation
    if (file.size > this.maxFileSize || 
      !extension || !allowedExtensions.includes(extension)) {

      this.fileSizeError = true;
      this.isFileValid = false;
      this.selectedFile = null;
      

      this.businessVerificationForm.patchValue({
        w9File: null
      });

      this.businessVerificationForm.get('w9File')?.setErrors({
        fileSize: true
      });

      return;
    }   

    // Valid file
    this.selectedFile = file;
    this.isFileValid = true;

    this.businessVerificationForm.patchValue({
      w9File: file
    });

    this.businessVerificationForm.get('w9File')?.setErrors(null);
  }

  onFileError(event: any): void {

    this.fileSizeError = true;
    this.isFileValid = false;
    this.selectedFile = null;

    this.businessVerificationForm.patchValue({
      w9File: null
    });

    this.businessVerificationForm.get('w9File')?.setErrors({
      fileSize: true
    });
  }

  onFileRemove(): void {

    this.selectedFile = null;
    this.fileSizeError = false;
    this.isFileValid = false;

    this.businessVerificationForm.patchValue({
      w9File: null
    });

    this.businessVerificationForm.get('w9File')?.setErrors({
      required: true
    });
  }

  submitBusinessVerification(): void {
    this.businessVerificationForm.markAllAsTouched();

    if (
      this.businessVerificationForm.invalid ||
      !this.companyId ||
      !this.selectedFile ||
      !this.isFileValid ||
      this.fileSizeError
    ) {
      return;
    }

    const ein = this.businessVerificationForm.get('ein')?.value;

    this.spinner.show();

    this.companyService
      .uploadNewW9(this.companyId, ein, this.selectedFile)
      .subscribe({
        next: () => {
          this.companyService
            .sendW9VerificationEmails(this.companyId)
            .subscribe({
              next: () => {
                this.showVerificationSuccess();
              },
              error: (err) => {
                console.error(
                  'Business Verification submitted, but email failed:',
                  err
                );

                // W-9 submission was successful.
                this.showVerificationSuccess();
              }
            });
        },
        error: (err) => {
          console.error('Failed to upload Business Verification:', err);

          this.spinner.hide();

          this.dialogSummary = Messages.ErrorTitle;
          this.dialogMsg =
            'Failed to upload Business Verification. Please try again.';
          this.dialogAction = 'none';
          this.locListModalDlg = true;
        }
      });
  }

  private showVerificationSuccess(): void {
    this.spinner.hide();

    this.verificationStatus = 'Pending';
    this.dialogSummary = 'Success';
    this.dialogMsg = 'Business Verification submitted successfully.';
    this.dialogAction = 'redirectToAccountSettings';
    this.locListModalDlg = true;
  }

  cancel(): void {

    this.router.navigate(['/home']);

  }

  stayOnPage(): void {

    this.locListModalDlg = false;

    if (this.resolveGuard) {

      this.resolveGuard(false);

    }

  }

  leavePage(): void {

    this.locListModalDlg = false;

    this.router.navigate(['/home']);

    if (this.resolveGuard) {

      this.resolveGuard(true);

    }

  }

  okClicked(): void {

    this.locListModalDlg = false;

    switch (this.dialogAction) {

      case 'redirectToAccountSettings':
        this.dialogAction = 'none';
        this.router.navigate(['/account-settings']);
        break;

      default:
        this.dialogAction = 'none';
        break;
    }

  }

}